import Axios from 'axios';
import { z } from 'zod';

/**
 * Coarse grouping the API assigns to each activity, so the UI can pick an icon
 * and colour without knowing every one of the ~85 activity labels.
 */
export enum ActivityCategory {
  JOB = 'JOB',
  FIELD = 'FIELD',
  DOCUMENT = 'DOCUMENT',
  COMMS = 'COMMS',
  REPORT = 'REPORT',
  FINANCE = 'FINANCE',
  EQUIPMENT = 'EQUIPMENT',
  TASK = 'TASK',
}

export const JobActivityActorSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
});

export type JobActivityActor = z.infer<typeof JobActivityActorSchema>;

export const JobActivityItemSchema = z.object({
  id: z.string(),
  /** The raw stored label, e.g. "Job status update" */
  activity: z.string(),
  /** Display line, rendered by the API, e.g. "Status changed to In progress" */
  title: z.string(),
  /** Optional second line, e.g. "To sam@example.com - Initial report" */
  detail: z.string().nullish(),
  // Not z.nativeEnum: a category added server-side must not fail the whole
  // response on an older build. The component falls back for unknown values.
  category: z.string(),
  occurredAt: z.string(),
  subjectType: z.string().nullish(),
  subjectId: z.string().nullish(),
  context: z.record(z.unknown()).nullish(),
  actor: JobActivityActorSchema.nullish(),
  /** How many raw events this row rolled up, e.g. "Photo note update ×3" */
  count: z.number().optional(),
  /**
   * Ids of up to 4 of the underlying events a rolled-up row represents, so
   * the preview pane can show more than just the primary one. Absent or a
   * single id for a row that wasn't grouped.
   */
  groupedEventIds: z.array(z.string()).optional(),
});

export type JobActivityItem = z.infer<typeof JobActivityItemSchema>;

export const JobActivityResponseSchema = z.object({
  items: z.array(JobActivityItemSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }),
});

export type JobActivityResponse = z.infer<typeof JobActivityResponseSchema>;

/**
 * Paginated activity history for a job, newest first.
 *
 * Restricted server-side to administrators, coordinators and supervisors.
 */
export async function getJobActivity({
  jobId,
  page = 1,
  pageSize = 20,
}: {
  jobId: string;
  page?: number;
  pageSize?: number;
}): Promise<JobActivityResponse> {
  const res = await Axios.get(`/api/v2/jobs/${jobId}/activity`, {
    params: { page, pageSize },
  });
  return JobActivityResponseSchema.parse(res.data);
}
